import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client.js";
import CustomDropdown from "../Components/StatusDropdown.js";
import { getAdminToken } from "./auth.js";

const EMPTY_CANDLE_PRODUCT = {
  fragranceNote: "",
  price: "",
  candleCount: "",
  packageWeight: "",
  packageLength: "",
  packageWidth: "",
  packageHeight: "",
  colorName: "",
  colorValue: "#c9a96e",
  mood: "",
  inStock: true,
  productIds: [],
};

const toApiCandleProduct = (form) => ({
  fragranceNote: form.fragranceNote.trim(),
  price: Number(form.price),
  candleCount: Number(form.candleCount),
  color: {
    name: form.colorName.trim(),
    value: form.colorValue.toLowerCase(),
  },
  mood: form.mood.trim(),
  packageWeight: Number(form.packageWeight),
  packageLength: Number(form.packageLength),
  packageWidth: Number(form.packageWidth),
  packageHeight: Number(form.packageHeight),
  inStock: form.inStock,
});

const toFormCandleProduct = (candleProduct) => ({
  fragranceNote: candleProduct.fragranceNote,
  price: String(candleProduct.price),
  candleCount: candleProduct.candleCount ? String(candleProduct.candleCount) : "",
  packageWeight: candleProduct.packageWeight
    ? String(candleProduct.packageWeight)
    : "",
  packageLength: candleProduct.packageLength
    ? String(candleProduct.packageLength)
    : "",
  packageWidth: candleProduct.packageWidth
    ? String(candleProduct.packageWidth)
    : "",
  packageHeight: candleProduct.packageHeight
    ? String(candleProduct.packageHeight)
    : "",
  colorName: candleProduct.color.name,
  colorValue: candleProduct.color.value,
  mood: candleProduct.mood,
  inStock: candleProduct.inStock !== false,
  productIds: (candleProduct.products || []).map((product) => product.id),
});

const getErrorMessage = (error) => {
  if (Array.isArray(error.details) && error.details.length > 0) {
    return error.details
      .map((detail) => `${detail.field}: ${detail.message}`)
      .join(" - ");
  }

  return error.message || "The candle set could not be saved";
};

const validateCandleProduct = (form) => {
  const price = Number(form.price);
  const candleCount = Number(form.candleCount);
  if (!form.fragranceNote.trim()) {
    throw new Error("Fragrance note is required.");
  }
  if (!Number.isFinite(price) || price < 0 || price > 10_000_000) {
    throw new Error("Price must be between 0 and 10000000.");
  }
  if (
    !Number.isInteger(candleCount) ||
    candleCount < 1 ||
    candleCount > 200
  ) {
    throw new Error("Candle count must be a whole number between 1 and 200.");
  }
  [
    ["packageWeight", "Package weight"],
    ["packageLength", "Package length"],
    ["packageWidth", "Package width"],
    ["packageHeight", "Package height"],
  ].forEach(([field, label]) => {
    const value = Number(form[field]);
    if (!form[field] || !Number.isFinite(value) || value <= 0) {
      throw new Error(`${label} must be greater than 0.`);
    }
  });
  if (!form.colorName.trim()) throw new Error("Color name is required.");
  if (!form.mood.trim()) throw new Error("Mood is required.");
};

function CandleProductForm({
  candleProduct = null,
  products = [],
  onCancel,
  onChanged,
  onUnauthorized,
}) {
  const [form, setForm] = useState(EMPTY_CANDLE_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const isEditing = Boolean(candleProduct?.id);

  const productOptions = useMemo(
    () =>
      products
        .filter((product) => product.productType !== "candle")
        .map((product) => ({
          label: product.name,
          value: product.id,
        })),
    [products],
  );

  useEffect(() => {
    setForm(
      candleProduct ? toFormCandleProduct(candleProduct) : EMPTY_CANDLE_PRODUCT,
    );
  }, [candleProduct]);

  const updateField = (field, value) => {
    setError("");
    setNotice("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleRequestError = (requestError) => {
    if (requestError.status === 401 || requestError.status === 403) {
      onUnauthorized();
      return;
    }
    setError(getErrorMessage(requestError));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      validateCandleProduct(form);
      const token = getAdminToken();
      const productIds = [...new Set(form.productIds)];

      if (isEditing) {
        await api.admin.candleProducts.update(
          candleProduct.id,
          toApiCandleProduct(form),
          token,
        );

        const previousProductIds = new Set(
          (candleProduct.products || []).map((product) => product.id),
        );
        const nextProductIds = new Set(productIds);
        const addedProductIds = productIds.filter(
          (productId) => !previousProductIds.has(productId),
        );
        const removedProductIds = [...previousProductIds].filter(
          (productId) => !nextProductIds.has(productId),
        );

        if (addedProductIds.length > 0) {
          await api.admin.candleProducts.linkProducts(
            candleProduct.id,
            addedProductIds,
            token,
          );
        }

        await Promise.all(
          removedProductIds.map((productId) =>
            api.admin.products.unlinkCandle(productId, candleProduct.id, token),
          ),
        );

        setNotice("Candle product updated successfully.");
      } else {
        const candleProductResponse = await api.admin.candleProducts.create(
          toApiCandleProduct(form),
          token,
        );
        const candleProductId = candleProductResponse.data?.id;

        if (!candleProductId) {
          throw new Error("Candle product was created, but its ID was not returned.");
        }

        if (productIds.length > 0) {
          await api.admin.candleProducts.linkProducts(
            candleProductId,
            productIds,
            token,
          );
        }

        setForm(EMPTY_CANDLE_PRODUCT);
        setNotice("Candle product created successfully.");
      }

      await onChanged?.(
        isEditing
          ? "Candle product updated successfully."
          : "Candle product created successfully.",
      );
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-product-form" onSubmit={handleSubmit}>
      <header>
        <div>
          <p className="admin-eyebrow">Candle set catalog</p>
          <h3>{isEditing ? "Edit candle set" : "Add candle set"}</h3>
          <p>Fields marked with * are required.</p>
        </div>
        {onCancel && (
          <button
            type="button"
            className="admin-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </header>

      {notice && (
        <p className="admin-form-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="admin-form-error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-form-grid">
        <label>
          <span>Fragrance note *</span>
          <input
            value={form.fragranceNote}
            onChange={(event) =>
              updateField("fragranceNote", event.target.value)
            }
            maxLength="300"
            required
          />
        </label>

        <label>
          <span>Candle count *</span>
          <input
            type="number"
            min="1"
            max="200"
            step="1"
            value={form.candleCount}
            onChange={(event) => updateField("candleCount", event.target.value)}
            required
          />
        </label>

        <label>
          <span>Price *</span>
          <input
            type="number"
            min="0"
            max="10000000"
            step="0.01"
            value={form.price}
            onChange={(event) => updateField("price", event.target.value)}
            required
          />
        </label>

        <label>
          <span>Package weight, lb *</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.packageWeight}
            onChange={(event) =>
              updateField("packageWeight", event.target.value)
            }
            required
          />
        </label>

        <label>
          <span>Package length, in *</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.packageLength}
            onChange={(event) =>
              updateField("packageLength", event.target.value)
            }
            required
          />
        </label>

        <label>
          <span>Package width, in *</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.packageWidth}
            onChange={(event) => updateField("packageWidth", event.target.value)}
            required
          />
        </label>

        <label>
          <span>Package height, in *</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.packageHeight}
            onChange={(event) =>
              updateField("packageHeight", event.target.value)
            }
            required
          />
        </label>

        <label>
          <span>Mood *</span>
          <input
            value={form.mood}
            onChange={(event) => updateField("mood", event.target.value)}
            maxLength="200"
            required
          />
        </label>

        <label>
          <span>Color name *</span>
          <input
            value={form.colorName}
            onChange={(event) => updateField("colorName", event.target.value)}
            maxLength="80"
            required
          />
        </label>

        <label>
          <span>Color *</span>
          <span className="admin-color-input">
            <input
              type="color"
              value={form.colorValue}
              onChange={(event) =>
                updateField("colorValue", event.target.value)
              }
              required
            />
            <input
              value={form.colorValue}
              onChange={(event) =>
                updateField("colorValue", event.target.value)
              }
              pattern="#[0-9A-Fa-f]{6}"
              required
            />
          </span>
        </label>

        <label>
          <span>Linked Products</span>
          <CustomDropdown
            multi
            searchable
            value={form.productIds}
            onChange={(productIds) => updateField("productIds", productIds)}
            options={productOptions}
            placeholder="Select products"
            className="admin-product-dropdown"
            disabled={saving || productOptions.length === 0}
          />
        </label>
      </div>

      <label className="admin-checkbox">
        <input
          type="checkbox"
          checked={form.inStock}
          onChange={(event) => updateField("inStock", event.target.checked)}
        />
        <span>Candle set is in stock</span>
      </label>

      <div className="admin-form-actions">
        <button type="submit" disabled={saving}>
          {saving
            ? "Saving..."
            : isEditing
              ? "Update candle set"
              : "Create candle set"}
        </button>
      </div>
    </form>
  );
}

export default CandleProductForm;
